import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import type { SimulationNode } from '../../../models/simulation';
import { useGraph } from '../../../store/simulationStore';
import { deriveFormulaUnit } from '../../../utils/formulaUnits';

interface NodeCompareSelectorProps {
  selectedNodeIds: string[];
  onSelectionChange: (nodeIds: string[]) => void;
}

// Derive unit for nodes
const getNodeUnit = (node: SimulationNode, allNodes: SimulationNode[]): string | undefined => {
  if (node.data.type === 'distribution') {
    return node.data.unit;
  }
  if (node.data.type === 'formula') {
    return deriveFormulaUnit(node, allNodes);
  }
  return undefined;
};

export const NodeCompareSelector = ({
  selectedNodeIds,
  onSelectionChange,
}: NodeCompareSelectorProps) => {
  const graph = useGraph();

  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const nodesById = useMemo(() => {
    const map = new Map<string, SimulationNode>();
    graph.nodes.forEach((node) => {
      map.set(node.id, node);
    });
    return map;
  }, [graph.nodes]);

  const selectedNodes = useMemo(() => {
    return selectedNodeIds
      .map((id) => nodesById.get(id))
      .filter((node): node is SimulationNode => Boolean(node));
  }, [selectedNodeIds, nodesById]);

  const availableNodes = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();
    const candidates = graph.nodes.filter((node) => !selectedNodeIds.includes(node.id));

    const filtered = normalizedQuery
      ? candidates.filter((node) => {
          const nameMatch = node.data.name?.toLowerCase().includes(normalizedQuery);
          const descriptionMatch = node.data.description
            ?.toLowerCase()
            .includes(normalizedQuery);
          const tagMatch = (node.data.tags || []).some((tag) =>
            tag.toLowerCase().includes(normalizedQuery),
          );
          return nameMatch || descriptionMatch || tagMatch;
        })
      : candidates;

    return filtered
      .sort((a, b) =>
        a.data.name.localeCompare(b.data.name, undefined, { sensitivity: 'base' }),
      )
      .slice(0, 10);
  }, [graph.nodes, query, selectedNodeIds]);

  const clearBlurTimeout = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  const handleFocus = useCallback(() => {
    clearBlurTimeout();
    setIsFocused(true);
  }, [clearBlurTimeout]);

  const handleBlur = useCallback(() => {
    clearBlurTimeout();
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
    }, 120);
  }, [clearBlurTimeout]);

  useEffect(() => {
    return () => {
      clearBlurTimeout();
    };
  }, [clearBlurTimeout]);

  const handleAddNode = useCallback(
    (nodeId: string) => {
      if (selectedNodeIds.includes(nodeId)) {
        return;
      }
      onSelectionChange([...selectedNodeIds, nodeId]);
      setQuery('');
      inputRef.current?.focus();
    },
    [onSelectionChange, selectedNodeIds],
  );

  const handleRemoveNode = useCallback(
    (nodeId: string) => {
      onSelectionChange(selectedNodeIds.filter((id) => id !== nodeId));
    },
    [onSelectionChange, selectedNodeIds],
  );

  const handleClearSelection = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  const showDropdown = isFocused;
  const dropdownHasResults = availableNodes.length > 0;

  return (
    <div className="node-compare-selector">
      <label className="scenarios-filter-label" htmlFor="node-compare-search">
        Nodes to compare
      </label>
      <div className="node-compare-search-wrapper">
        <div className={`node-compare-search ${showDropdown ? 'is-focused' : ''}`}>
          <span className="node-compare-icon">
            <Search size={16} aria-hidden="true" />
          </span>
          <input
            ref={inputRef}
            id="node-compare-search"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Search nodes by name, description, or tag"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="node-compare-input-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {showDropdown && (
          <div
            className="node-compare-dropdown"
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            {dropdownHasResults ? (
              <div className="node-compare-results">
                {availableNodes.map((node) => {
                  const unit = getNodeUnit(node, graph.nodes);
                  return (
                    <button
                      key={node.id}
                      type="button"
                      className="node-search-item"
                      onClick={() => handleAddNode(node.id)}
                    >
                      <div className="node-search-item-title">
                        {node.data.name}
                        {unit && <span className="node-search-item-unit"> {unit}</span>}
                      </div>
                      {node.data.description && (
                        <div className="node-search-item-description">
                          {node.data.description}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="node-compare-empty">
                <p>No matching nodes</p>
                <span>Try a different search term.</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="selected-nodes-summary">
        <div className="selected-nodes-header">
          <span>
            Selected nodes
            {selectedNodes.length > 0 && (
              <span className="selected-nodes-count"> ({selectedNodes.length})</span>
            )}
          </span>
          {selectedNodes.length > 0 && (
            <button
              type="button"
              className="node-compare-clear-selection"
              onClick={handleClearSelection}
            >
              Clear all
            </button>
          )}
        </div>
        {selectedNodes.length === 0 ? (
          <p className="hint">Use the search to add nodes you want to compare.</p>
        ) : (
          <div className="selected-nodes-chips">
            {selectedNodes.map((node) => (
              <span key={node.id} className="selected-node-chip">
                {node.data.name}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={() => handleRemoveNode(node.id)}
                  aria-label={`Remove ${node.data.name}`}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


